import AdminHeader from '@/components/v2/molecules/admin/layout/AdminHeader';
import AdminSidebar from '@/components/v2/molecules/admin/layout/AdminSidebar';

import { adminTabs, visibleTabsByRole } from '@/constants/adminTabs';
import { Role } from '@/constants/rolePermissions';

import { AdminNavigationProvider } from '@/contexts/AdminNavigationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';

const AdminLayout = () => {
  const { profile } = useAuth();
  const isMobile = useIsMobile();

  const filteredTabs = useMemo(() => {
    if (!profile) return [];

    const roleTabs = visibleTabsByRole[profile.role as Role];
    
    if (!roleTabs) return [];

    return adminTabs.filter((tab) => roleTabs.includes(tab.key));
  }, [profile]);

  const [activeTab, setActiveTab] = useState(filteredTabs[0]?.key ?? '');
  
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => {
    const saved = localStorage.getItem('admin-sidebar-expanded');
    return saved ? JSON.parse(saved) : false;
  });

  // Persistir estado del sidebar en localStorage
  useEffect(() => {
    localStorage.setItem('admin-sidebar-expanded', JSON.stringify(isSidebarExpanded));
  }, [isSidebarExpanded]);

  // Sincronizar activeTab cuando filteredTabs cambia
  useEffect(() => {
    if (filteredTabs.length > 0 && !filteredTabs.find(t => t.key === activeTab)) {
      setActiveTab(filteredTabs[0].key);
    }
  }, [filteredTabs, activeTab]);

  return (
    <AdminNavigationProvider
      tabs={filteredTabs}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      isSidebarExpanded={isSidebarExpanded}
      setSidebarExpanded={setIsSidebarExpanded}
    >
      {!isMobile ? (
        // Layout Desktop con Sidebar
        <div className="flex h-screen w-full overflow-hidden" data-admin-internal>
          <AdminSidebar />
          
          <div className={cn(
            "flex flex-col flex-1 transition-all duration-300 overflow-hidden",
            isSidebarExpanded ? "ml-64" : "ml-14"
          )}>
            <main
              className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col"
              data-admin-internal
            >
              <Outlet />
            </main>
          </div>
        </div>
      ) : (
        // Layout Mobile (sin cambios)
        <div className="flex flex-col h-screen" data-admin-internal>
          <AdminHeader />

          <main
            className="flex-1 overflow-y-auto flex flex-col"
            data-admin-internal
          >
            <Outlet />
          </main>
        </div>
      )}
    </AdminNavigationProvider>
  );
};

export default AdminLayout;
